import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { GraduationCap, Plus, Users, Clock } from 'lucide-react'
import { api } from '../lib/api'
import { queries } from '../lib/queries'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Card, CardContent } from '../components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Skeleton } from '../components/ui/skeleton'

export const Route = createFileRoute('/admin/schools/')({
  head: () => ({
    meta: [{ title: 'Schools | Admin | Unbreakable' }],
  }),
  component: AdminSchoolsPage,
})

function AdminSchoolsPage() {
  const queryClient = useQueryClient()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [formData, setFormData] = useState({ name: '', slug: '', timezone: 'America/New_York', seatLimit: 100 })
  const [formError, setFormError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: queries.admin.schools(),
    queryFn: () => api.getAdminSchools(),
  })

  const schools = data?.schools ?? []

  const createMutation = useMutation({
    mutationFn: (data: { name: string; slug: string; timezone?: string; seatLimit?: number }) =>
      api.createAdminSchool(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.admin.schools() })
      setShowCreateDialog(false)
      setFormData({ name: '', slug: '', timezone: 'America/New_York', seatLimit: 100 })
      setFormError(null)
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Failed to create school'),
  })

  const handleCreate = () => {
    if (!formData.name.trim() || !formData.slug.trim()) { setFormError('Name and slug are required'); return }
    setFormError(null)
    createMutation.mutate({ name: formData.name.trim(), slug: formData.slug.trim().toLowerCase(), timezone: formData.timezone || undefined, seatLimit: formData.seatLimit || undefined })
  }

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Schools</h1>
          <p className="text-gray-500">Create and manage school accounts</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />Create School
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-gray-900">{schools.length}</div><p className="text-sm text-gray-500">Total Schools</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{schools.reduce((sum, s) => sum + (s.staffCount ?? 0), 0)}</div><p className="text-sm text-gray-500">Total Staff</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-brand">{schools.reduce((sum, s) => sum + (s.seatUsage ?? 0), 0)}</div><p className="text-sm text-gray-500">Total Enrolled Devices</p></CardContent></Card>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">All Schools</h2>
        </div>

        {isLoading ? (
          <div className="p-6 space-y-3">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : schools.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No schools yet</p>
            <p className="text-sm">Create your first school to get started.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>School</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Seats</TableHead>
                <TableHead>Timezone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schools.map((school) => (
                <TableRow key={school.id}>
                  <TableCell>
                    <Link to="/admin/schools/$id" params={{ id: school.id }} className="hover:underline">
                      <div>
                        <span className="font-medium text-gray-900">{school.name}</span>
                        <p className="text-xs text-gray-500 font-mono">{school.slug}</p>
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Users className="h-3.5 w-3.5 text-gray-400" />
                      {school.staffCount ?? 0}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{school.seatUsage ?? 0} / {school.seatLimit}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="h-3.5 w-3.5" />
                      {school.timezone || '—'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={school.billingStatus === 'active' ? 'default' : 'outline'}>
                      {school.billingStatus || 'No billing'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {new Date(school.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) { setFormError(null); setFormData({ name: '', slug: '', timezone: 'America/New_York', seatLimit: 100 }) } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create School</DialogTitle>
            <DialogDescription>Set up a new school account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {formError && <div className="rounded-lg bg-red-50 border border-red-200 p-3"><p className="text-sm text-red-700">{formError}</p></div>}
            <div>
              <Label>School Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => {
                  const name = e.target.value
                  setFormData({ ...formData, name, slug: generateSlug(name) })
                }}
                placeholder="Springfield Elementary"
              />
            </div>
            <div>
              <Label>Slug *</Label>
              <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} placeholder="springfield-elementary" className="font-mono" />
              <p className="text-xs text-gray-500 mt-1">URL-safe identifier, auto-generated from name.</p>
            </div>
            <div>
              <Label>Timezone</Label>
              <Input value={formData.timezone} onChange={(e) => setFormData({ ...formData, timezone: e.target.value })} placeholder="America/New_York" />
            </div>
            <div>
              <Label>Seat Limit</Label>
              <Input type="number" value={formData.seatLimit} onChange={(e) => setFormData({ ...formData, seatLimit: parseInt(e.target.value, 10) || 0 })} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreate} disabled={createMutation.isPending} className="flex-1">
                {createMutation.isPending ? 'Creating...' : 'Create School'}
              </Button>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
